import { useState } from "react";
import { fmtSize, toFullUrl } from "../../utils/helper";
import { onsite_url } from "../../config";

/* ── Styles ─────────────────────────────────────────── */
const S = {
  page: {
    background: "#f4f6f9",
    minHeight: "100vh",
    fontFamily: "'DM Sans', sans-serif",
  },
  container: { padding: "28px 28px 0" },
  topBar: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 10,
  },
  breadcrumb: {
    fontSize: 12,
    color: "#9aa5b4",
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: "uppercase",
    marginBottom: 3,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a2332",
    margin: 0,
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 18px",
    border: "1.5px solid #e3e8ef",
    borderRadius: 8,
    background: "#fff",
    color: "#4a5568",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    transition: "all 0.15s",
  },
  /* section card — overflow removed so react-select menus are not clipped */
  sectionCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e3e8ef",
    marginBottom: 20,
    /* overflow: "hidden" intentionally removed — was clipping Select dropdowns */
    boxShadow: "0 1px 8px rgba(26,107,60,0.05)",
  },
  sectionHeader: {
    padding: "14px 20px",
    borderBottom: "1px solid #f0f4f8",
    display: "flex",
    alignItems: "center",
    gap: 10,
    /* keep rounded top corners explicitly since overflow:hidden is gone */
    borderRadius: "14px 14px 0 0",
  },
  sectionIcon: {
    width: 30,
    height: 30,
    borderRadius: 8,
    background: "#e8f5ee",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    color: "#1a6b3c",
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#1a2332",
    margin: 0,
  },
  sectionBody: { padding: "20px 20px 8px" },
  /* form controls */
  label: {
    fontSize: 12.5,
    fontWeight: 700,
    color: "#4a5568",
    marginBottom: 6,
    display: "block",
    letterSpacing: 0.2,
  },
  requiredStar: { color: "#c0392b", marginLeft: 2 },
  uuidWrap: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    border: "1.5px solid #e3e8ef",
    borderRadius: 8,
    overflow: "hidden",
    background: "#f8fafb",
  },
  uuidInput: {
    flex: 1,
    border: "none",
    background: "transparent",
    padding: "8px 12px",
    fontSize: 12.5,
    fontFamily: "monospace",
    color: "#6b7a8d",
    outline: "none",
  },
  uuidLabel: {
    padding: "6px 12px",
    background: "#e8f5ee",
    color: "#1a6b3c",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    borderLeft: "1px solid #d0e4d8",
  },
  /* receipt info card */
  receiptCard: {
    background: "linear-gradient(135deg, #f5fbf7 0%, #eaf5f0 100%)",
    border: "1.5px solid #c8e6d8",
    borderRadius: 10,
    padding: "16px 18px",
    marginTop: 12,
    marginBottom: 16,
  },
  receiptHeader: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#1a6b3c",
    marginBottom: 12,
  },
  receiptGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 10,
  },
  receiptItem: {
    background: "#fff",
    border: "1px solid #d8edd6",
    borderRadius: 8,
    padding: "10px 12px",
  },
  receiptItemLabel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "#7aab8a",
    marginBottom: 3,
  },
  receiptItemValue: {
    fontSize: 13.5,
    fontWeight: 500,
    color: "#1a2332",
    wordBreak: "break-all",
  },
  /* participant types */
  typesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
  },
  typeCol: {},
  typeColHeader: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: "#9aa5b4",
    marginBottom: 10,
    paddingBottom: 6,
    borderBottom: "2px solid #f0f4f8",
  },
  typeRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "7px 0",
    borderBottom: "1px solid #f8fafb",
  },
  typeLabel: {
    fontSize: 13,
    color: "#2d3748",
    fontWeight: 500,
  },
  typeInput: {
    width: 68,
    textAlign: "center",
    border: "1.5px solid #e3e8ef",
    borderRadius: 7,
    padding: "5px 8px",
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
    color: "#1a2332",
    fontWeight: 600,
    transition: "border-color 0.15s",
  },
  /* buttons */
  btnRow: {
    display: "flex",
    gap: 10,
    paddingTop: 4,
    paddingBottom: 28,
    justifyContent: "flex-end",
  },
  cancelBtn: {
    padding: "10px 22px",
    border: "1.5px solid #e3e8ef",
    borderRadius: 8,
    background: "#fff",
    color: "#4a5568",
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    transition: "all 0.15s",
  },
  submitBtn: {
    padding: "10px 28px",
    border: "none",
    borderRadius: 8,
    background: "#1a6b3c",
    color: "#fff",
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    transition: "all 0.15s",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
  fileZone: {
    border: "2px dashed #d8e4ec", borderRadius: 10, padding: "18px 16px",
    textAlign: "center", cursor: "pointer", transition: "all 0.15s",
    background: "#fafcfe", position: "relative",
  },
  fileInput: {
    position: "absolute", inset: 0, opacity: 0,
    cursor: "pointer", width: "100%", height: "100%",
  },
};

/* ── Select theme override ── */
export const customSelectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    fontSize: 13.5,
    borderColor: hasError ? "#c0392b" : state.isFocused ? "#1a6b3c" : "#e3e8ef",
    boxShadow: state.isFocused
      ? hasError
        ? "0 0 0 3px rgba(192,57,43,0.12)"
        : "0 0 0 3px rgba(26,107,60,0.12)"
      : "none",
    borderRadius: 8,
    fontFamily: "'DM Sans', sans-serif",
    "&:hover": { borderColor: hasError ? "#c0392b" : "#1a6b3c" },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13.5,
    fontFamily: "'DM Sans', sans-serif",
    background: state.isSelected
      ? "#1a6b3c"
      : state.isFocused
      ? "#e8f5ee"
      : "#fff",
    color: state.isSelected ? "#fff" : "#1a2332",
  }),
  singleValue: (base) => ({ ...base, color: "#1a2332" }),
  placeholder: (base) => ({ ...base, color: "#9aa5b4", fontSize: 13.5 }),
  menu: (base) => ({ ...base, borderRadius: 8, overflow: "hidden" }),
  /* ✅ ensure the portal-rendered menu sits above everything */
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

/* ── Section wrapper ── */
export function Section({ icon, title, children }) {
  return (
    <div style={S.sectionCard}>
      <div style={S.sectionHeader}>
        <div style={S.sectionIcon}>{icon}</div>
        <h3 style={S.sectionTitle}>{title}</h3>
      </div>
      <div style={S.sectionBody}>{children}</div>
    </div>
  );
}

/* ── Field wrapper ── */
export function Field({ label, required, error, children, col = "col-md-4" }) {
  return (
    <div className={`${col} mb-3`}>
      <label style={S.label}>
        {label}
        {required && <span style={S.requiredStar}>*</span>}
      </label>
      {children}
      {error && (
        <div style={{ fontSize: 12, color: "#c0392b", marginTop: 4 }}>
          {error}
        </div>
      )}
    </div>
  );
}


export function FileUploadZone({ name, label, accept, onChange, value, onRemoveStored }) {
  const [hovered,  setHovered]  = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const storedFiles = Array.isArray(value)
    ? value.filter((v) => v && !(v instanceof File))
    : [];
  const newFiles = value instanceof File
    ? [value]
    : Array.isArray(value) ? value.filter((v) => v instanceof File) : [];
  const hasAny = storedFiles.length > 0 || newFiles.length > 0;

  /* ── Drag & drop ── */
  const handleDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = ()  => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;
    const dt = new DataTransfer();
    files.forEach((f) => dt.items.add(f));
    onChange({ target: { name, files: dt.files } });
  };

  return (
    <div>
      {/* Drop zone */}
      <div
        style={{
          ...S.fileZone,
          borderColor: dragOver ? "#1a6b3c" : hovered ? "#1a6b3c" : hasAny ? "#a8d5b8" : "#d8e4ec",
          background:  dragOver ? "#e8f5ee" : hovered ? "#f0fbf5" : hasAny ? "#f5fbf7" : "#fafcfe",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input type="file" name={name} accept={accept} onChange={onChange} style={S.fileInput} multiple />
        <span style={{ fontSize: 22, display: "block", marginBottom: 6 }}>
          {dragOver ? "📂" : hasAny ? "✅" : ""}
        </span>
        <span style={{ fontSize: 13, color: "#6b7a8d", fontWeight: 500, display: "block", marginBottom: 2 }}>
          {dragOver ? "Drop files here" : hasAny ? "File attached — click or drag to add / replace" : label}
        </span>
        <span style={{ fontSize: 11.5, color: "#b0bec8" }}>PNG, JPG — max 10 MB • Drag &amp; drop supported</span>
      </div>

      {/* ── Existing stored files ── */}
      {storedFiles.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#1a6b3c", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>
            Existing {storedFiles.length > 1 ? `${storedFiles.length} files` : "file"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {storedFiles.map((f, i) => {
              // ✅ Use toFullUrl so relative DB paths get onsite_url prefix
              const url   = toFullUrl(f.url, onsite_url);
              const isImg = f.mimetype?.startsWith("image/");
              const isPdf = f.mimetype === "application/pdf";
              return (
                <div key={i} style={{ position: "relative", width: 110, borderRadius: 8, border: "1.5px solid #e3e8ef", overflow: "hidden", background: "#fff" }}>
                  <div style={{ width: 110, height: 80, background: "#f4f6f9", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>

                    {isImg ? (
                      <img
                        src={url} 
                        alt={f.originalName}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => {
                          // Show fallback icon if image fails to load
                          e.target.style.display = "none";
                          if (e.target.nextElementSibling) {
                            e.target.nextElementSibling.style.display = "flex";
                          }
                        }}
                      />
                    ) : null}

                    {/* Fallback / non-image icons */}
                    <div style={{ display: isImg ? "none" : "flex", flexDirection: "column", alignItems: "center", color: isPdf ? "#c0392b" : "#2563eb" }}>
                      {isPdf ? (
                        <>
                          <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0121 9.414V19a2 2 0 01-2 2z" />
                          </svg>
                          <span style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>PDF</span>
                        </>
                      ) : isImg ? (
                        // Image fallback placeholder (shown via onError)
                        <>
                          <span style={{ fontSize: 24 }}>🖼️</span>
                          <span style={{ fontSize: 9, color: "#9aa5b4", marginTop: 2 }}>No preview</span>
                        </>
                      ) : (
                        <>
                          <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>DOC</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Name */}
                  <div style={{ padding: "4px 6px", fontSize: 10, color: "#4a5568", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", borderTop: "1px solid #f0f4f8" }} title={f.originalName}>
                    {f.originalName || f.filename || "File"}
                    {f.size && <div style={{ fontSize: 9, color: "#9aa5b4" }}>{fmtSize(f.size)}</div>}
                  </div>

                  {/* View */}
                  <a href={url} target="_blank" rel="noreferrer"
                    style={{ display: "block", textAlign: "center", padding: "3px 6px", fontSize: 10.5, fontWeight: 600, color: "#1a6b3c", background: "#f5fbf7", textDecoration: "none", borderTop: "1px solid #f0f4f8" }}>
                    View ↗
                  </a>

                  {/* Remove */}
                  {onRemoveStored && (
                    <button type="button" onClick={() => onRemoveStored(name, i)} title="Remove"
                      style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "rgba(192,57,43,0.85)", color: "#fff", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Newly selected files ── */}
      {newFiles.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>
            New — will upload on save
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {newFiles.map((file, i) => {
              const isImg   = file.type?.startsWith("image/");
              const isPdf   = file.type === "application/pdf";
              const blobUrl = URL.createObjectURL(file);
              return (
                <div key={i} style={{ position: "relative", width: 110, borderRadius: 8, border: "1.5px solid #c8dcf8", overflow: "hidden", background: "#fff" }}>
                  <div style={{ position: "absolute", top: 4, left: 4, background: "#1a6b3c", color: "#fff", fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 4, letterSpacing: 0.5, textTransform: "uppercase" }}>New</div>
                  <div style={{ width: 110, height: 80, background: "#eff5ff", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                    {isImg ? (
                      <img src={blobUrl} alt={file.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : isPdf ? (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", color: "#c0392b" }}>
                        <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0121 9.414V19a2 2 0 01-2 2z" />
                        </svg>
                        <span style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>PDF</span>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", color: "#2563eb" }}>
                        <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>DOC</span>
                      </div>
                    )}
                  </div>
                  <div style={{ padding: "4px 6px", fontSize: 10, color: "#4a5568", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", borderTop: "1px solid #f0f4f8" }} title={file.name}>
                    {file.name}
                    <div style={{ fontSize: 9, color: "#9aa5b4" }}>{fmtSize(file.size)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}